/**
 * The scaled-space pass (M3 plan R8, spec §5, D-TBD 5): a separate scene at 1:1000 holding the system's star,
 * planets, atmospheres and rings. It renders first into an HDR (HalfFloat) target with its own depth, and that
 * target becomes the local scene's background, so the post pipeline is unchanged and ships and rocks always draw in
 * front. It replaces M1's fixed-distance impostors in reach mode.
 *
 * - The scaled camera sits at the scaled origin with the main camera's rotation, fov and aspect; bodies sit at
 *   (body − camera) × scale, subtracted in float64 before the float32 cast (D-011 rebasing).
 * - Depth: near 0.5 and far 2e8 scaled units with the renderer's reversed-Z.
 * - The scaled scene's background is the baked sky (R3's `env.skyBackground`); the sky's star layer moves into the
 *   scaled scene while attached (pushed out near the far plane) so planets hide the stars behind them.
 * - Lighting: a point light at the star (decay 0) plus a faint ambient.
 * - Sphere LOD: one shared unit sphere per LOD row; each frame a body picks its row by angular radius.
 *
 * `update` is allocation-free (D-014); `setSystem` (a system switch) allocates and then compiles the pipelines.
 */
import {
  AmbientLight,
  Group,
  HalfFloatType,
  Mesh,
  PerspectiveCamera,
  PlaneGeometry,
  PointLight,
  RenderTarget,
  RingGeometry,
  Scene,
  SphereGeometry,
  Vector3,
  type Node,
  type Object3D,
  type Quaternion,
  type WebGPURenderer,
} from 'three/webgpu'
import { screenUV, texture } from 'three/tsl'
import { logger } from '../../core/logger'
import type { Vec3 } from '../../core/math'
import type { QualityPreset, TuningGroup } from '../contracts'
import { loadEnvironmentData } from '../environment/data'
import { atmosphereLookFor, loadScaledConfig, planetLookFor, type ScaledConfig } from './config'
import {
  createAtmosphereMaterial,
  createPlanetMaterial,
  createRingMaterial,
  createSharedUniforms,
  createStarMaterials,
  seedOffset,
  type SharedUniforms,
  type StarMaterials,
} from './materials'
import { angularRadius, glowShellScale, lodIndex, minSizeBoost, rebaseScaled, spinAngle, starLightIntensity } from './math'
import type { ScaledBodySpec, ScaledSystemSpec } from './spec'

export type { ScaledBodySpec, ScaledStarSpec, ScaledSystemSpec } from './spec'
export { scaledSpecFromLayout } from './spec'
export { loadScaledConfig, scaledConfigSchema, type ScaledConfig } from './config'
export { angularRadius, atmosphereRadius, glowShellScale, lodIndex, minSizeBoost, rebaseScaled, ringRadii, sphereSegmentsFor, spinAngle, starLightIntensity } from './math'

export interface ScaledStats {
  /** Last frame's scaled-pass time in ms (CPU submit time: the renderer is created without GPU timestamps). */
  renderMs: number
  /** Bodies drawn (planets plus the star; 0 with no system). */
  bodies: number
  /** Triangles in the current sphere LODs, rings and billboards. */
  triangles: number
}

export interface ScaledSpace {
  readonly scene: Scene
  readonly camera: PerspectiveCamera
  readonly tuning: TuningGroup
  readonly stats: ScaledStats
  /** Builds the system's meshes (null clears it), then compiles the pipelines. */
  setSystem(spec: ScaledSystemSpec | null): Promise<void>
  /** Scaled background = `sky`; the main scene's background = the scaled target. */
  attach(mainScene: Scene, sky: Node<'vec4'>): void
  detach(): void
  /** Camera pose (float64 world position, rotation, vertical fov in degrees, aspect) and time. Allocation-free. */
  update(cam: Readonly<Vec3>, quat: Quaternion, fovDeg: number, aspect: number, timeS: number): void
  render(): void
  /** Drawing-buffer size in CSS pixels × pixel ratio; the target is that × the preset's resolution. */
  setSize(width: number, height: number, pixelRatio: number): void
  setPreset(preset: QualityPreset): void
  dispose(): void
}

/** Granulation time wraps here so the float32 uniform stays precise over long sessions. */
const TIME_WRAP_S = 3600
/** The moved sky star layer sits at this fraction of the far plane (behind every body of a ≤ 0.3 AU system). */
const STAR_LAYER_FAR_FRACTION = 0.5
const Y_AXIS = new Vector3(0, 1, 0)
const X_AXIS = new Vector3(1, 0, 0)
const DEG2RAD = Math.PI / 180

type BackgroundScene = Scene & { backgroundNode: Node | null }

// ---------------------------------------------------------------------------------------------------------------
// Frame state (the allocation-free per-frame path, exported for tests)
// ---------------------------------------------------------------------------------------------------------------

export interface BodyView {
  readonly spec: ScaledBodySpec
  /** Positioned (rebased) and tilted; scale = the body radius in scaled units. */
  readonly group: Group
  /** Spins about the group's local Y. Its geometry is the current sphere LOD. */
  readonly surface: Mesh
  readonly atmosphere: Mesh
  readonly ring: Mesh | null
  /** The kind's atmosphere shell scale (planet radii) at normal size. */
  readonly atmosphereScale: number
  lod: number
}

export interface StarView {
  readonly group: Group
  readonly surface: Mesh
  readonly corona: Mesh
  readonly glare: Mesh
  readonly streak: Mesh
  position: readonly [number, number, number]
  radiusM: number
  lod: number
}

export interface ScaledFrame {
  readonly cfg: ScaledConfig
  readonly camera: PerspectiveCamera
  readonly shared: SharedUniforms
  readonly light: PointLight
  /** One unit sphere per LOD row. */
  readonly spheres: readonly SphereGeometry[]
  /** Triangles per LOD row. */
  readonly sphereTris: readonly number[]
  /** Triangles of the rings and billboards (constant per system). */
  fixedTris: number
  star: StarView | null
  readonly bodies: BodyView[]
  readonly stats: ScaledStats
  /** The viewport height in CSS pixels (the minimum on-screen size is set in pixels); 0 = unknown, no minimum. */
  viewportHeightPx: number
}

/** Places the camera, the star and every body for this frame; picks sphere LODs; spins bodies. Allocation-free. */
export function updateScaledFrame(s: ScaledFrame, cam: Readonly<Vec3>, quat: Quaternion, fovDeg: number, aspect: number, timeS: number): void {
  const camera = s.camera
  camera.quaternion.copy(quat)
  if (camera.fov !== fovDeg || camera.aspect !== aspect) {
    camera.fov = fovDeg
    camera.aspect = aspect
    camera.updateProjectionMatrix()
  }
  s.shared.time.value = timeS % TIME_WRAP_S
  const star = s.star
  if (star === null) return
  const scale = s.cfg.scale
  const th = s.cfg.lod.thresholdsRad
  const minSize = s.cfg.minSize
  const minAng = s.viewportHeightPx > 0 ? (minSize.radiusPx * fovDeg * DEG2RAD) / s.viewportHeightPx : 0
  let tris = s.fixedTris

  const sp = star.position
  rebaseScaled(sp[0], sp[1], sp[2], cam, scale, star.group.position)
  s.shared.starPos.value.copy(star.group.position)
  s.light.position.copy(star.group.position)
  const sx = sp[0] - cam.x
  const sy = sp[1] - cam.y
  const sz = sp[2] - cam.z
  star.lod = lodIndex(angularRadius(star.radiusM, Math.sqrt(sx * sx + sy * sy + sz * sz)), th)
  star.surface.geometry = s.spheres[star.lod]
  tris += s.sphereTris[star.lod]

  const bodies = s.bodies
  for (let i = 0; i < bodies.length; i++) {
    const b = bodies[i]
    const p = b.spec.position
    rebaseScaled(p[0], p[1], p[2], cam, scale, b.group.position)
    const bx = p[0] - cam.x
    const by = p[1] - cam.y
    const bz = p[2] - cam.z
    const ang = angularRadius(b.spec.radiusM, Math.sqrt(bx * bx + by * by + bz * bz))
    const boost = minSizeBoost(ang, minAng)
    b.group.scale.setScalar(b.spec.radiusM * scale * boost)
    b.atmosphere.scale.setScalar(glowShellScale(b.atmosphereScale, minSize.glowScale, boost))
    b.lod = lodIndex(ang * boost, th)
    b.surface.geometry = s.spheres[b.lod]
    tris += s.sphereTris[b.lod] * 2 // surface + atmosphere shell (the shell uses the same row)
    b.atmosphere.geometry = s.spheres[b.lod]
    b.surface.quaternion.setFromAxisAngle(Y_AXIS, spinAngle(timeS, b.spec.rotationPeriodS))
  }
  s.stats.triangles = tris
}

// ---------------------------------------------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------------------------------------------

function trianglesOf(g: SphereGeometry | RingGeometry | PlaneGeometry): number {
  const index = g.getIndex()
  return index === null ? g.getAttribute('position').count / 3 : index.count / 3
}

interface BuiltBody {
  view: BodyView
  dispose(): void
}

function buildBody(spec: ScaledBodySpec, cfg: ScaledConfig, shared: SharedUniforms, sphere: SphereGeometry, ringGeo: RingGeometry): BuiltBody {
  const group = new Group()
  group.name = `scaled:body:${spec.id}`
  group.scale.setScalar(spec.radiusM * cfg.scale)
  group.quaternion.setFromAxisAngle(X_AXIS, spec.tiltRad)
  const surfaceMat = createPlanetMaterial(spec.kind, planetLookFor(cfg, spec.kind), spec.seed)
  const surface = new Mesh(sphere, surfaceMat.material)
  surface.name = `scaled:surface:${spec.id}`
  surface.frustumCulled = false
  const atmoLook = atmosphereLookFor(cfg, spec.kind)
  const atmoMat = createAtmosphereMaterial(atmoLook, shared)
  const atmosphere = new Mesh(sphere, atmoMat.material)
  atmosphere.name = `scaled:atmosphere:${spec.id}`
  atmosphere.scale.setScalar(atmoLook.scale)
  atmosphere.frustumCulled = false
  atmosphere.renderOrder = 1
  group.add(surface, atmosphere)
  let ring: Mesh | null = null
  let ringDispose = (): void => {}
  if (spec.rings) {
    const ringMat = createRingMaterial(cfg.rings, spec.seed, shared)
    ring = new Mesh(ringGeo, ringMat.material)
    ring.name = `scaled:ring:${spec.id}`
    ring.frustumCulled = false
    ring.renderOrder = 2
    group.add(ring)
    ringDispose = (): void => ringMat.material.dispose()
  }
  return {
    view: { spec, group, surface, atmosphere, ring, atmosphereScale: atmoLook.scale, lod: 0 },
    dispose(): void {
      surfaceMat.material.dispose()
      atmoMat.material.dispose()
      ringDispose()
      group.removeFromParent()
    },
  }
}

interface StarLayerMove {
  readonly mesh: Object3D
  readonly parent: Object3D
  readonly scale: number
}

export function createScaledSpace(renderer: WebGPURenderer, preset: QualityPreset): ScaledSpace {
  const cfg = loadScaledConfig()
  const scene = new Scene() as BackgroundScene
  scene.name = 'scaled'
  // PERF L-M1: rendered once a frame; the graph is walked explicitly before that render and the boot compile, not inside three.
  scene.matrixWorldAutoUpdate = false
  scene.backgroundNode = null
  const camera = new PerspectiveCamera(60, 1, cfg.near, cfg.far)
  camera.name = 'scaled:camera'
  const target = new RenderTarget(1, 1, { type: HalfFloatType, depthBuffer: true })
  target.texture.name = 'scaled:target'
  const mainBackground = texture(target.texture, screenUV)

  const shared = createSharedUniforms()
  const light = new PointLight(0xffffff, cfg.light.intensity, 0, 0)
  light.name = 'scaled:starLight'
  const ambient = new AmbientLight(0xffffff, cfg.light.ambient)
  scene.add(light, ambient)

  const spheres = cfg.lod.segments.map((seg) => new SphereGeometry(1, seg, Math.max(8, Math.round(seg / 2))))
  const sphereTris = spheres.map(trianglesOf)
  const ringGeo = new RingGeometry(cfg.rings.inner, cfg.rings.outer, cfg.rings.segments, 2)
  ringGeo.rotateX(-Math.PI / 2)
  const quad = new PlaneGeometry(1, 1)

  // The star: one set of views and materials, re-tinted per system.
  const starMats: StarMaterials = createStarMaterials(cfg.star, shared)
  const starGroup = new Group()
  starGroup.name = 'scaled:star'
  const starSurface = new Mesh(spheres[0], starMats.surface)
  starSurface.name = 'scaled:star:surface'
  const corona = new Mesh(quad, starMats.corona)
  corona.name = 'scaled:star:corona'
  corona.renderOrder = 3
  const glare = new Mesh(quad, starMats.glare)
  glare.name = 'scaled:star:glare'
  glare.renderOrder = 4
  const streak = new Mesh(quad, starMats.streak)
  streak.name = 'scaled:star:streak'
  streak.renderOrder = 4
  for (const m of [starSurface, corona, glare, streak]) m.frustumCulled = false
  // The surface scales with the star radius; the billboards stay unscaled (their shaders size themselves).
  starGroup.add(starSurface, corona, glare, streak)
  starGroup.visible = false
  scene.add(starGroup)

  const stats: ScaledStats = { renderMs: 0, bodies: 0, triangles: 0 }
  const frame: ScaledFrame = {
    cfg,
    camera,
    shared,
    light,
    spheres,
    sphereTris,
    fixedTris: 0,
    star: null,
    bodies: [],
    stats,
    viewportHeightPx: 0,
  }
  const starView: StarView = { group: starGroup, surface: starSurface, corona, glare, streak, position: [0, 0, 0], radiusM: 1, lod: 0 }
  let built: BuiltBody[] = []
  let lightScale = 1
  const size = { width: 1, height: 1, pixelRatio: 1 }
  let resolution = cfg.resolution[preset]
  let attached: { main: BackgroundScene; previous: Node | null; stars: StarLayerMove | null } | null = null
  const skyRadius = loadEnvironmentData().sky.radius

  const resize = (): void => {
    const w = Math.max(1, Math.floor(size.width * size.pixelRatio * resolution))
    const h = Math.max(1, Math.floor(size.height * size.pixelRatio * resolution))
    if (target.width !== w || target.height !== h) target.setSize(w, h)
  }

  const clearBodies = (): void => {
    for (const b of built) b.dispose()
    built = []
    frame.bodies.length = 0
  }

  const tuning: TuningGroup = {
    name: 'Scaled space',
    params: {
      starIntensity: { value: cfg.star.intensity, min: 1, max: 200, step: 1 },
      corona: { value: cfg.star.corona.intensity, min: 0, max: 20, step: 0.1 },
      glare: { value: cfg.star.glare.intensity, min: 0, max: 6, step: 0.05 },
      streak: { value: cfg.star.streak.intensity, min: 0, max: 4, step: 0.05 },
      starLight: { value: cfg.light.intensity, min: 0, max: 10, step: 0.05 },
      ambient: { value: cfg.light.ambient, min: 0, max: 0.5, step: 0.005 },
      atmosphere: { value: 1, min: 0, max: 4, step: 0.05 },
      rings: { value: 1, min: 0, max: 2, step: 0.05 },
    },
    apply(): void {
      const n = (k: string): number => {
        const v = tuning.params[k]?.value
        return typeof v === 'number' ? v : 0
      }
      starMats.uniforms.intensity.value = n('starIntensity')
      starMats.uniforms.corona.value = n('corona')
      starMats.uniforms.glare.value = n('glare')
      starMats.uniforms.streak.value = n('streak')
      cfg.light.intensity = n('starLight')
      light.intensity = cfg.light.intensity * lightScale
      ambient.intensity = n('ambient')
      shared.atmosphereGain.value = n('atmosphere')
      shared.ringGain.value = n('rings')
    },
  }

  const space: ScaledSpace = {
    scene,
    camera,
    tuning,
    stats,
    async setSystem(spec: ScaledSystemSpec | null): Promise<void> {
      clearBodies()
      if (spec === null) {
        frame.star = null
        starGroup.visible = false
        stats.bodies = 0
        stats.triangles = 0
        return
      }
      const t0 = performance.now()
      const st = spec.star
      starView.position = st.position
      starView.radiusM = st.radiusM
      starGroup.scale.setScalar(1)
      starSurface.scale.setScalar(st.radiusM * cfg.scale)
      starMats.uniforms.radius.value = st.radiusM * cfg.scale
      starMats.uniforms.color.value.set(st.color)
      seedOffset(st.seed, starMats.uniforms.offset.value)
      light.color.set(st.color)
      lightScale = starLightIntensity(cfg.light, st.luminosity) / Math.max(cfg.light.intensity, 1e-6)
      light.intensity = cfg.light.intensity * lightScale
      starGroup.visible = true
      frame.star = starView

      let fixed = trianglesOf(quad) * 3
      for (const b of spec.bodies) {
        const body = buildBody(b, cfg, shared, spheres[0], ringGeo)
        scene.add(body.view.group)
        built.push(body)
        frame.bodies.push(body.view)
        if (body.view.ring) fixed += trianglesOf(ringGeo)
      }
      frame.fixedTris = fixed
      stats.bodies = spec.bodies.length + 1
      const buildMs = performance.now() - t0

      // Compile against the scaled target (the render target is captured synchronously, before the first await).
      const t1 = performance.now()
      const previous = renderer.getRenderTarget()
      renderer.setRenderTarget(target)
      scene.updateMatrixWorld()
      const compiling = renderer.compileAsync(scene, camera)
      renderer.setRenderTarget(previous)
      await compiling
      logger.info(`Scaled space: ${spec.bodies.length} bodies built in ${buildMs.toFixed(1)} ms, compiled in ${(performance.now() - t1).toFixed(1)} ms`)
    },
    attach(mainScene: Scene, sky: Node<'vec4'>): void {
      if (attached !== null) space.detach()
      const main = mainScene as BackgroundScene
      scene.backgroundNode = sky
      const previous = main.backgroundNode ?? null
      main.backgroundNode = mainBackground
      // The sky's star layer (R3, 'sky:stars') would draw over the scaled planets from the main scene: move it into
      // the scaled scene and push it out (its model scale scales its view distance) behind every body.
      const mesh = mainScene.getObjectByName('sky:stars')
      let stars: StarLayerMove | null = null
      if (mesh !== undefined && mesh.parent !== null) {
        stars = { mesh, parent: mesh.parent, scale: mesh.scale.x }
        scene.add(mesh)
        mesh.scale.setScalar((cfg.far * STAR_LAYER_FAR_FRACTION) / skyRadius)
      }
      attached = { main, previous, stars }
    },
    detach(): void {
      if (attached === null) return
      const a = attached
      if (a.main.backgroundNode === mainBackground) a.main.backgroundNode = a.previous
      scene.backgroundNode = null
      if (a.stars !== null) {
        a.stars.parent.add(a.stars.mesh)
        a.stars.mesh.scale.setScalar(a.stars.scale)
      }
      attached = null
    },
    update(cam: Readonly<Vec3>, quat: Quaternion, fovDeg: number, aspect: number, timeS: number): void {
      updateScaledFrame(frame, cam, quat, fovDeg, aspect, timeS)
    },
    render(): void {
      const t0 = performance.now()
      const previous = renderer.getRenderTarget()
      renderer.setRenderTarget(target)
      scene.updateMatrixWorld()
      renderer.render(scene, camera)
      renderer.setRenderTarget(previous)
      stats.renderMs = performance.now() - t0
    },
    setSize(width: number, height: number, pixelRatio: number): void {
      size.width = width
      size.height = height
      size.pixelRatio = pixelRatio
      frame.viewportHeightPx = height
      resize()
    },
    setPreset(p: QualityPreset): void {
      resolution = cfg.resolution[p]
      resize()
    },
    dispose(): void {
      space.detach()
      clearBodies()
      starMats.dispose()
      for (const g of spheres) g.dispose()
      ringGeo.dispose()
      quad.dispose()
      target.dispose()
      scene.clear()
    },
  }
  return space
}
